import React from 'react';
import styled from 'styled-components';
import { AttachmentIcon } from '../icons/AttachmentIcon';
import { CloseFilledIcon } from '../icons/CloseFilledIcon';
import { DownloadIconBig } from '../icons/DownloadIconBig';

const StyledFlex = styled.div.attrs(props => ({ ...props }))`
    box-sizing: border-box;
    display: flex;
    flex-direction: ${({ direction }) => direction || 'row'};
    align-items:  ${({ align }) => align};
    justify-content:  ${({ justify }) => justify};
    position: ${({ position}) => position};
    height: ${props => props.height};
    width: ${props => props.width};
    margin: ${({margin}) => margin && `${margin.top || 0} ${margin.right || 0} ${margin.bottom || 0} ${margin.left || 0} `};
    padding: ${({padding}) => padding && `${padding.top || 0} ${padding.right || 0} ${padding.bottom || 0} ${padding.left || 0} `};
    background: ${({ background }) => background};
    white-space: ${({ whiteSpace}) => whiteSpace};
    flex: ${({flex}) => flex};
    flex-wrap: ${({wrap}) => wrap};
    cursor: ${({ cursor }) => cursor};

    & > span:not(:last-child),
    & > div:not(:last-child),
    & > button:not(:last-child),
    & > a:not(:last-child),
    & > i:not(:last-child) {
        margin-right: ${({gap}) => gap && gap.right && `${gap.right}px`};
        margin-bottom: ${({gap}) => gap && gap.bottom && `${gap.bottom}px`};
    };
`;

export const Flex = (props) => {
    return <StyledFlex {...props} />
}

const StyledTop = styled(Flex).attrs({
    height: "64px",
    align: "center",
    justify: "space-between",
    background: "#FFFFFF"
})`
    box-shadow: 0px 8px 24px rgba(116, 151, 208, 0.1);
    z-index: 100;
    padding: 12px 40px;
    min-width: 1126px;
`;

export const Top = (props) => {
    return <StyledTop {...props} />
}

const StyledBox = styled(Flex).attrs(props => ({
    direction: props.direction ? props.direction : "column",
    background: props.background ? props.background : "#FFFFFF"
}))`
    padding: 22px 32px;
    border-radius: 20px;
    border: ${({error}) => error && "2px solid var(--sp-error-color)"};
    box-shadow: ${({ flatten }) => !flatten && "0px 16px 48px rgba(116, 151, 208, 0.1)"};
`;

const StyledBoxLoading = styled.div`
    min-height: ${({height}) => height || "144px"};
    background: linear-gradient(-45deg, #E0E8F9 23%, rgba(256, 256, 256, .1) 37%, #E0E8F9 59%);
    background-size: 400% 100%;
    border-radius: 20px;
    animation: loading 3s ease 0s infinite normal none;

    @-webkit-keyframes loading {
        0% {
            background-position: 100% 50%;
        }
        100% {
            background-position: 0 50%;
        }
    }

    @keyframes loading {
        0% {
            background-position: 100% 50%;
        }
        100% {
            background-position: 0 50%;
        }
    }
`;

export const Box = (props) => {
    return(props.flatten && props.isLoading ? <StyledBoxLoading />  : <StyledBox {...props} />)
};

const StyledBorderedField = styled(Flex).attrs(({ align, ...props }) => ({
    align: align ? align : "center",
    ...props
}))`
    border: ${({hasError}) => `2px solid ${hasError ? "var(--sp-error-color)" : "#E4EDFD"}`};
    border-radius: 8px;
    max-width: ${({maxWidth}) => maxWidth};

    &:hover, &:focus {
        border-color: ${({hasError}) => hasError ? "var(--sp-error-hover-color)" : "#d2e1fc"};
    }
`;

export const BorderedField = (props) => <StyledBorderedField {...props} />;

const StyledUserBox = styled(Flex).attrs({
    direction: "column",
    justify: "center"
})`
    font-weight: 600;
    font-size: 14px;
    line-height: 18px;
    color: #4C5F7D;
`;

export const UserBox = (props) => <StyledUserBox {...props} />

const StyledActionText = styled(Flex).attrs(props => ({
    gap: { right: 4 },
    align: "center",
    cursor: "pointer"
}))`
    font-size: 14px;
    line-height: 157%;
    color: ${({danger}) => danger ? "var(--sp-danger-color)" : "var(--sp-main-color-hover)"};
    white-space: nowrap;

    &:hover {
        color: ${({danger}) => danger ? "var(--sp-danger-hover-color)" : "var(--sp-main-color)"};
    }
`;

export const ActionText = (props) => <StyledActionText {...props} />

const StyledAttachmentBox = styled(Flex).attrs({
    direction: "column",
    position: "relative",
    margin: { left: "8px", top: "8px", right: "8px", bottom: "8px" }
})`

`;

const StyledRemoveAttachment = styled.span`
    position: absolute;
    right: 0;
    padding: 6px;
    color: var(--sp-grey-blue-color);

    &: hover {
        cursor: pointer;
        color: var(--sp-error-color);
    }

    &:active > i {
        transform: translateY(1px);
    }
`;

const StyledAttachmentBoxIcon = styled(Box).attrs({
    flatten: true,
    background: "var(--sp-light-grey-blue-color)",
    align: "center",
    justify: "center",
    height: "100px",
    width: "140px",
    margin: { bottom: "4px" }
})`
    &:hover > span {
        visibility: visible;
    }
`;

const StyledDownloadIcon = styled.span`
    position: absolute;
    padding: 12px;
    background: var(--sp-light-grey-blue-color);
    color: var(--sp-grey-blue-color);
    cursor: pointer;
    visibility: hidden;

    &:active > i {
        transform: translateY(1px);
    }
`;

export const AttacmentBox = ({ removeAttachment, downloadAttachment, ...props}) => (
    <StyledAttachmentBox>
        <StyledRemoveAttachment onClick={() => removeAttachment()}>
            <CloseFilledIcon />
        </StyledRemoveAttachment>
        <StyledAttachmentBoxIcon>
            <StyledDownloadIcon onClick={() => downloadAttachment()}>
                <DownloadIconBig />
            </StyledDownloadIcon>
            <AttachmentIcon />
        </StyledAttachmentBoxIcon>
        {props.children}
    </StyledAttachmentBox>
);